/**
 * ВНИМАНИЕ! Это служебный модуль и его НЕ НАДО использовать для логирования.
 * Для логирования используйте:
 * - в `res/js/app` модуль `OK/StatLogger` или `OK/logger` (последний, если не нужна предобработка логируемых данных);
 * - в `apps/projects` модуль `ok/statistics/StatLogger`.
 */

import type { Nullable } from '@ok/interfaces/global/Nullable';
import type { IOK } from '@ok/interfaces/ok/IOK';
import { readCookie } from 'OK/cookie';
import * as DebugErrorServiceModule from 'OK/DebugErrorService';
import { DataCache } from 'OK/DataCache';
import { GwtExperimentsCfg } from 'OK/GwtExperimentsCfg';

declare var OK: IOK;

/**
 * Настройки логирования.
 */
export interface ILoggerConfig {
    /**
     * Включено ли логирование больших объемов данных.
     * См. one.app.community.dk.conf.GwtLoggerConfiguration.isGwtLogClobCfgEnabled
     */
    clobLogEnabled: boolean;
    /**
     * Список названий операций, для которых включено логирование больших объемов данных.
     * См. one.app.community.dk.conf.GwtLoggerConfiguration.getGwtClobOperations
     */
    clobOperations: string[];
    /**
     * Список названий операций, логирование для которых заблокировано.
     * См. one.app.community.dk.conf.GwtLoggerConfiguration.getGwtLogOperationsDisabled
     */
    disabledLogOperations: string[];
    /**
     * Включено ли логирование.
     * См. one.app.community.dk.conf.GwtLoggerConfiguration.isGwtLogEnabled
     */
    enabled: boolean;
    /**
     * Список названий логируемых операций, которые фильтруются в зависимости от времени:
     * при поступлении в интервал времени нескольких одинаковых записей вида операция+параметр1+параметр2
     * будет залогировано только первое из них.
     * См. one.app.community.dk.conf.GwtLoggerConfiguration.getUserUniqueOperations
     */
    filterByTimeOperations: string[];
    /**
     * Включает или отключает незамедлительную отправку записей, добавляемых в лог.
     *
     * Внимание! Данная настройка предназначена для служебных целей и её не следует использовать без крайней необходимости.
     */
    immediateSending: boolean;
    /**
     * Список названий логируемых операций.
     * См. one.app.community.dk.conf.GwtLoggerConfiguration.getGwtLogOperations
     */
    logOperations: string[];
    /**
     * Список названий операций, для которых включено логирование данных любого типа (отличных от строки).
     * См. one.app.community.dk.conf.GwtLoggerConfiguration.getGwtRawOperations
     */
    rawOperations: string[];
}

const loggerConfig: ILoggerConfig = {
    clobLogEnabled: true,
    clobOperations: [],
    disabledLogOperations: [],
    enabled: true,
    filterByTimeOperations: [],
    immediateSending: false,
    logOperations: [],
    rawOperations: []
};

const ERROR_LOG = 'error';
const GARBAGE_LOG = 'garbagelog';
const LOG_URL = '/gwtlog';
const UNIQUE_OPERATION_PERIOD = 300000;   // 5 минут

const uniqueOperationCache = new DataCache<boolean>({
    clearInterval: UNIQUE_OPERATION_PERIOD,
    sizeLimit: 1000
});

/**
 * Обновляет текущие настройки логирования на основе переданных значений.
 *
 * @param config
 *      Задает настройки, которые надо изменить.
 */
export function updateLoggerConfig(config: Partial<ILoggerConfig>) {
    for (let field in config) {
        // Игнорируем значения null и undefined, поскольку в конфиге их быть не должно
        if (config.hasOwnProperty(field) && config[field as keyof ILoggerConfig] != null) {
            // @ts-ignore TS2322: Type boolean | string[] | undefined is not assignable to type boolean & string[]
            loggerConfig[field as keyof ILoggerConfig] = config[field as keyof ILoggerConfig];
        }
    }
}

function isDebugEnabled(){
    const debug = readCookie('dme');
    return (debug && (debug.toLowerCase() === 'true')) || document.getElementById('__gwtd__m') != null;
}

/**
 * Отправляет на сервер накопленный лог.
 */
export function sendLog() {
    const data = OK.logging.repository.getRequestData();
    if (data) {
        OK.sendBeacon(LOG_URL, data);
    }
}

function getPrefix(operation: string): string {
    const index = operation.indexOf('.');
    return index === -1
        ? operation
        : operation.substring(0, index);
}

function isGarbageLogEnabled(operation: string) {
    return loggerConfig.logOperations.includes(GARBAGE_LOG) && !loggerConfig.disabledLogOperations.includes(operation);
}

function isLogEnabled(operation: string): boolean {
    return Boolean(operation) && loggerConfig.enabled && loggerConfig.logOperations.includes(operation);
}

function isClobLogEnabled(operation: string): boolean {
    return loggerConfig.clobLogEnabled || loggerConfig.clobOperations.includes(operation);
}

function logOperation(
    operation: string,
    duration: number | Nullable,
    info: string | Nullable,
    clob: string | Nullable,
    rawData: unknown,
    forceSend: boolean,
    ...params: unknown[]
) {
    const prefix = getPrefix(operation);
    if (!isLogEnabled(prefix)) {
        if (isGarbageLogEnabled(prefix)) {
            success(GARBAGE_LOG, prefix);
        }
        return;
    }

    if (loggerConfig.filterByTimeOperations.includes(operation)) {
        const operationKey = operation + '<->'
                + (params.length > 0 ? params[0] : '') + '<->'
                + (params.length > 1 ? params[1] : '');
        // Если аналогичные данные уже логировались недавно, то игнорируем запись
        if (uniqueOperationCache.get(operationKey)) {
            return;
        }
        // В кэше данные считаются устаревшими при выполнении условия saveTime + lifeTime < nowTime
        // В связи с этим уменьшаем lifeTime на 1, чтобы получить нужный период хранения.
        uniqueOperationCache.set(operationKey, true, UNIQUE_OPERATION_PERIOD - 1);
    }

    if (clob && !isClobLogEnabled(operation)) {
        clob = null;
    }
    // В rawData могут быть любые данные, поэтому считаем, что они есть, если значение отличается от null и undefined
    if (rawData != null && !loggerConfig.rawOperations.includes(operation)) {
        rawData = null;
    }

    OK.logging.repository.addLog(operation, duration, info, clob, rawData, params[0], params[1]);

    if (forceSend || loggerConfig.immediateSending) {
        sendLog();
    }
}

function showError(
    id: string,
    stacktrace: string | Nullable,
    exceptionName: unknown,
    throwableClassName: string,
    place: unknown,
    message: string,
    operation: string,
    timestamp: number,
    alertMessage: string
) {
    const errorPlace = place != null
        ? String(place)
        : '';
    import('OK/DebugErrorService')
        .then(
            function(serviceModule: typeof DebugErrorServiceModule) {
                const debugErrorService = serviceModule.getInstance();
                const error = debugErrorService.createDefaultErrorItem({
                    id,
                    stacktrace: stacktrace != null
                        ? stacktrace
                        : undefined,
                    exceptionName: exceptionName != null
                        ? String(exceptionName)
                        : '',
                    throwableClassName,
                    place: errorPlace,
                    message,
                    operation,
                    timestamp,
                    alertMessage
                });
                debugErrorService.showError(error);
            }
        )
        // Если не удалось загрузить DebugErrorService
        .catch(
            function() {
                if (GwtExperimentsCfg.getInstance().isShownAlertForThisError(errorPlace, operation)) {
                    const message =  "Fatal Error '" + operation + "'\n" +
                        "place='" + place + "'\n" +
                        "error name='" + exceptionName + "'\n" +
                        "CLOB='" + clob + "'";

                    if (OK.fn.isAlertsEnabled()) {
                        window.alert(message);
                    } else {
                        console.log(message);
                    }
                }
            }
        );
}

/**
 * Записывает в лог сведения об ошибке.
 *
 * @param errorLocation
 *      Место, где произошла ошибка.
 * @param errorMessage
 *      Сообщение об ошибке.
 */
export function error(errorLocation: string, errorMessage: string) {
    logOperation(ERROR_LOG, null, '', null, null, false, errorMessage, errorLocation);
}

/**
 * Записывает в лог сведения об успешном выполнении операции.
 *
 * @param operation
 *      Название операции.
 * @param param1
 *      Дополнительные сведения для записи в лог.
 * @param param2
 *      Дополнительные сведения для записи в лог.
 * @param rawData
 *      Дополнительные сведения для записи в лог.
 */
export function success(operation: string, param1?: unknown, param2?: unknown, rawData?: unknown) {
    logOperation(operation, null, null, null, rawData, false, param1, param2);
}

/**
 * Записывает в лог сведения об успешном выполнении операции и вызывает отправку накопленного лога на сервер.
 *
 * @param operation
 *      Название операции.
 * @param param1
 *      Дополнительные сведения для записи в лог.
 * @param param2
 *      Дополнительные сведения для записи в лог.
 */
export function force(operation: string, param1?: string, param2?: string) {
    logOperation(operation, null, null, null, null, true, param1, param2);
}

/**
 * Записывает в лог сведения об успешном выполнении операции с указанием её продолжительности.
 *
 * @param operation
 *      Название операции.
 * @param duration
 *      Продолжительность операции.
 * @param params
 *      Дополнительные сведения для записи в лог.
 */
export function duration(operation: string, duration: number, ...params: unknown[]) {
    // В GwtOperationLogger.java ожидается, что duration будет целым числом
    logOperation(operation, Math.floor(duration), null, null, null, false, ...params);
}

/**
 * Записывает в лог сведения об ошибке вместе с сопутствующими строковыми данными (например, stacktrace).
 *
 * @param operation
 *      Название ошибки.
 * @param clob
 *      Данные, связанные с ошибкой.
 * @param params
 *      Дополнительные сведения для записи в лог.
 */
export function clob(operation: string, clob: string | null | undefined, ...params: unknown[]) {
    // Проверка для okweb-success - наследие GWT (https://jira.odkl.ru/browse/OL-85671). Возможно, уже не актуально.
    if (isDebugEnabled() && params[0] !== 'okweb-success') {
        // Не выводим alert для неизвестных ошибок. Скорее всего, они прилетели из внешних скриптов
        if (operation !== 'undefined-error') {
            const timestamp = Date.now();
            const errorId = 'Logger_clob_' + timestamp;
            const alertMessage = 'Fatal Error. Clob. ' + operation;

            showError(errorId, clob, params[1], '', params[0], '', operation, timestamp, alertMessage);
        }
        if (clob) {
            console.log(clob);
        }
    }

    logOperation(operation, null, null, clob, null, true, ...params);
}

/**
 * Записывает в лог сведения об операции вместе с данными об ошибке.
 *
 * @param operation
 *      Название операции.
 * @param info
 *      Дополнительные сведения для записи в лог.
 * @param applicationPartWhereExcCaught
 *      Место, где возникло событие.
 * @param errorName
 *      Название ошибки.
 */
export function trace(operation: string, info: string, applicationPartWhereExcCaught: string, errorName: string) {
    logOperation(operation, null, info, null, null, false, errorName, applicationPartWhereExcCaught);
}

/**
 * Записывает в лог сведения об операции вместе с дополнительными данными.
 *
 * @param operation
 *      Название операции.
 * @param data
 *      Дополнительные данные для записи в лог.
 */
export function raw(operation: string, data: object) {
    logOperation(operation, null, null, null, data, false);
}
